import { useCallback, useState, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router";

/** A value in localStorage, for the filter bar. */
export function useStored<T extends string = string>(key: string, fallback: T): [T, (value: T) => void] {
	const [value, setValue] = useState<T>(() => {
		try {
			return (localStorage.getItem(key) as T) ?? fallback;
		} catch {
			// Private mode, or storage blocked. The panel works without it.
			return fallback;
		}
	});

	const set = useCallback(
		(next: T) => {
			setValue(next);
			try {
				localStorage.setItem(key, next);
			} catch {
				/* not worth surfacing */
			}
		},
		[key],
	);

	return [value, set];
}

/**
 * Whether a CSS media query matches, kept in step with the viewport.
 *
 * `useSyncExternalStore` rather than an effect: the first render has to agree
 * with the query, and a state set from an effect gives it one frame of the
 * wrong answer — which for a layout question is a visible flash.
 */
export function useMedia(query: string): boolean {
	const subscribe = useCallback(
		(onChange: () => void) => {
			if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
			const list = window.matchMedia(query);
			list.addEventListener("change", onChange);
			return () => list.removeEventListener("change", onChange);
		},
		[query],
	);
	const read = useCallback(() => {
		if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
		return window.matchMedia(query).matches;
	}, [query]);
	return useSyncExternalStore(subscribe, read, () => false);
}

/**
 * A screen's open tab, as `?tab=` — so a reload, a link or a back button lands
 * on the tab somebody was reading. The first tab is the bare path, and a value
 * no tab owns falls back to it rather than drawing an empty panel.
 */
export function useTabParam<T extends string>(tabs: readonly T[]): [T, (value: string) => void] {
	const [params, setParams] = useSearchParams();
	const raw = params.get("tab");
	const tab = tabs.find((one) => one === raw) ?? tabs[0];
	const set = useCallback(
		(next: string) =>
			setParams(
				(current) => {
					const out = new URLSearchParams(current);
					if (next === tabs[0]) out.delete("tab");
					else out.set("tab", next);
					return out;
				},
				{ replace: true },
			),
		[setParams, tabs],
	);
	return [tab, set];
}
